import { useEffect, useState } from 'react';
import type { ExperienceProduct } from '@flyingmuse/shared';
import { getExperiences } from '../lib/booking';

type Status = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Live experiences from the LinkTwist booking engine. Fetched once and shared
 * across the app via a module-level cache. This is an *enhancement* over the
 * static experience copy — every consumer must work fine if it never loads.
 */
let cache: ExperienceProduct[] | null = null;
let inflight: Promise<ExperienceProduct[]> | null = null;
const listeners = new Set<() => void>();

function load(): Promise<ExperienceProduct[]> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = getExperiences()
      .then((res) => {
        cache = res.experiences;
        listeners.forEach((l) => l());
        return cache;
      })
      .catch((err) => {
        inflight = null; // allow a later retry
        throw err;
      });
  }
  return inflight;
}

export interface UseExperiences {
  products: ExperienceProduct[];
  byAlias: (alias: string) => ExperienceProduct | undefined;
  status: Status;
}

export function useExperiences(): UseExperiences {
  const [status, setStatus] = useState<Status>(cache ? 'ready' : 'idle');
  const [, force] = useState(0);

  useEffect(() => {
    let active = true;
    if (cache) {
      setStatus('ready');
      return;
    }
    setStatus('loading');
    load()
      .then(() => {
        if (active) setStatus('ready');
      })
      .catch(() => {
        if (active) setStatus('error');
      });

    const onUpdate = () => active && force((n) => n + 1);
    listeners.add(onUpdate);
    return () => {
      active = false;
      listeners.delete(onUpdate);
    };
  }, []);

  const products = cache ?? [];
  return {
    products,
    byAlias: (alias) => products.find((p) => p.alias === alias),
    status,
  };
}
